import { useState } from "react";

import { format } from "date-fns";
import { ArrowUp, ArrowDown, Square, CheckSquare } from "lucide-react";

import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { H2, P } from "~/components/ui/typography";
import { cn } from "~/lib/utils";

import { ATTRIBUTE_LIST } from "../utils/part-types";
import type { ConfidenceLevel } from "../../parts-similarity/utils/confidence";

// AIDEV-NOTE: Shared components for part similarity review dialogs
// Used by both SinglePartSimilarityReview and AutoLinkReviewDialog

// --- Confidence Dots ---
// Returns dot indicators for confidence level (signal strength style)

export function ConfidenceDots({
    confidence,
}: {
    confidence: ConfidenceLevel | number;
}) {
    const normalized =
        typeof confidence === "number"
            ? confidence >= 0.8
                ? "high"
                : confidence >= 0.5
                  ? "medium"
                  : "low"
            : confidence;

    const filledCount =
        normalized === "high" ? 3 : normalized === "medium" ? 2 : 1;
    const color =
        normalized === "high"
            ? "bg-green-600"
            : normalized === "medium"
              ? "bg-amber-500"
              : "bg-muted-foreground";

    return (
        <div className="flex items-center gap-0.5">
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    className={cn(
                        "size-2 rounded-full",
                        i < filledCount ? color : "bg-muted",
                    )}
                />
            ))}
        </div>
    );
}

// --- Current Part Details ---
// Left-side content showing the current part interaction details

export interface CurrentPartDetailsProps {
    name: string;
    description?: string | null;
    recap?: string | null;
    quote?: string | null;
    attributes?: Array<{ name: string; content: string }> | null;
}

export function CurrentPartDetails({
    name,
    description,
    recap,
    quote,
    attributes,
}: CurrentPartDetailsProps) {
    return (
        <Card className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto">
                <div className="p-4 space-y-4">
                    <div>
                        <H2 className="mb-1">{name}</H2>
                        {description && (
                            <P className="text-sm text-muted-foreground">
                                {description}
                            </P>
                        )}
                    </div>

                    {recap && (
                        <div>
                            <P className="text-xs font-medium text-muted-foreground mb-1">
                                Session Recap
                            </P>
                            <P className="text-sm">{recap}</P>
                        </div>
                    )}

                    {quote && (
                        <div>
                            <P className="text-xs font-medium text-muted-foreground mb-1">
                                Quote
                            </P>
                            <P className="text-sm italic border-l-2 border-muted pl-3">
                                "{quote}"
                            </P>
                        </div>
                    )}

                    {attributes && attributes.length > 0 && (
                        <div className="space-y-2">
                            {ATTRIBUTE_LIST.map((attr) => {
                                const attributeData = attributes.find(
                                    (a) =>
                                        a.name === attr.key ||
                                        attr.alternateKeys?.includes(a.name),
                                );
                                const value = attributeData?.content;

                                if (!value || value === "Unclear") return null;

                                return (
                                    <div key={attr.key}>
                                        <P className="text-xs text-muted-foreground">
                                            {attr.label}
                                        </P>
                                        <P className="text-sm">{value}</P>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}

// --- Expandable Match Card ---
// Card showing a potential match with expandable details

export interface MatchData {
    partId: string;
    partName?: string;
    partDescription?: string | null;
    confidence: ConfidenceLevel | number;
    reason?: string;
    reasoning?: string;
    differences?: string | null;
    existingPart?: {
        name?: string;
        description?: string | null;
    };
    latestInteraction?: {
        sessionDate: string;
        sessionTitle: string;
        recap?: string | null;
        quote?: string | null;
        attributes?: Array<{ name: string; content: string }>;
    };
}

export interface ExpandableMatchCardProps {
    match: MatchData;
    isSelected?: boolean;
    onSelect?: () => void;
    defaultExpanded?: boolean;
    showCheckbox?: boolean;
}

export function ExpandableMatchCard({
    match,
    isSelected = false,
    onSelect,
    defaultExpanded = false,
    showCheckbox = true,
}: ExpandableMatchCardProps) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const partName =
        match.partName ||
        match.existingPart?.name ||
        `Part ID: ${match.partId}`;
    const partDescription =
        match.partDescription || match.existingPart?.description;
    const latestInteraction = match.latestInteraction;
    const confidence = match.confidence;
    const reasoning = match.reason || match.reasoning || "";
    const differences = match.differences;

    const handleClick = () => {
        if (onSelect) {
            onSelect();
        }
    };

    return (
        <div
            className={cn(
                "flex items-center gap-3",
                onSelect && "cursor-pointer",
            )}
            onClick={handleClick}
        >
            {/* Checkbox indicator outside card */}
            {showCheckbox && (
                <span className="shrink-0 text-muted-foreground">
                    {isSelected ? (
                        <CheckSquare className="size-5 text-primary" />
                    ) : (
                        <Square className="size-5" />
                    )}
                </span>
            )}
            <Card
                className={cn(
                    "flex-1 transition-all overflow-hidden !p-0 !mb-0 !space-y-0",
                    isSelected && "border-gray-400 shadow-md",
                )}
            >
                <CardHeader className="p-3">
                    <div className="flex-1 min-w-0">
                        {/* Part name with confidence dots */}
                        <div className="flex items-center gap-2 mb-4">
                            <ConfidenceDots confidence={confidence} />
                            <span className="font-medium truncate">
                                {partName}
                            </span>
                        </div>
                        {/* Preview of reasoning */}
                        {reasoning && (
                            <P
                                className={cn(
                                    "text-sm mt-1 line-clamp-3 mb-4",
                                    isSelected
                                        ? "text-foreground"
                                        : "text-muted-foreground",
                                )}
                            >
                                {reasoning}
                            </P>
                        )}
                        {/* See more on new line */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(!isExpanded);
                            }}
                            className="text-xs text-muted-foreground hover:text-foreground underline mt-1"
                        >
                            {isExpanded ? "See less" : "See more"}
                        </button>
                    </div>
                </CardHeader>

                {isExpanded && (
                    <CardContent className="px-3 pb-3 pt-0">
                        <div className="space-y-3">
                            {/* Reasoning */}
                            <div className="space-y-1">
                                <div className="flex items-start gap-2">
                                    <ArrowUp className="mt-0.5 size-3.5 shrink-0 text-green-600" />
                                    <P className="text-sm text-muted-foreground">
                                        {reasoning}
                                    </P>
                                </div>
                                {differences && (
                                    <div className="flex items-start gap-2">
                                        <ArrowDown className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                                        <P className="text-sm text-muted-foreground">
                                            {differences}
                                        </P>
                                    </div>
                                )}
                            </div>

                            {/* Description */}
                            {partDescription && (
                                <div>
                                    <P className="text-xs font-medium text-muted-foreground mb-1">
                                        Description
                                    </P>
                                    <P className="text-sm">{partDescription}</P>
                                </div>
                            )}

                            {/* Session Recap - same style as CurrentPartDetails */}
                            {latestInteraction?.recap && (
                                <div>
                                    <P className="text-xs font-medium text-muted-foreground mb-1">
                                        Session Recap
                                    </P>
                                    <P className="text-sm">
                                        {latestInteraction.recap}
                                    </P>
                                </div>
                            )}

                            {/* Quote - same style as CurrentPartDetails */}
                            {latestInteraction?.quote && (
                                <div>
                                    <P className="text-xs font-medium text-muted-foreground mb-1">
                                        Quote
                                    </P>
                                    <P className="text-sm italic border-l-2 border-muted pl-3">
                                        "{latestInteraction.quote}"
                                    </P>
                                </div>
                            )}

                            {/* Attributes from latest interaction */}
                            {latestInteraction?.attributes &&
                                latestInteraction.attributes.length > 0 && (
                                    <div>
                                        <P className="text-xs font-medium text-muted-foreground mb-1">
                                            Attributes from{" "}
                                            {latestInteraction.sessionTitle}
                                        </P>
                                        <div className="space-y-2">
                                            {ATTRIBUTE_LIST.map((attr) => {
                                                const attributesArray =
                                                    latestInteraction.attributes ||
                                                    [];
                                                const attributeData =
                                                    attributesArray.find(
                                                        (a) =>
                                                            a.name ===
                                                                attr.key ||
                                                            attr.alternateKeys?.includes(
                                                                a.name,
                                                            ),
                                                    );
                                                const value =
                                                    attributeData?.content;

                                                if (
                                                    !value ||
                                                    value === "Unclear"
                                                )
                                                    return null;

                                                return (
                                                    <div key={attr.key}>
                                                        <P className="text-xs text-muted-foreground">
                                                            {attr.label}
                                                        </P>
                                                        <P className="text-sm">
                                                            {value}
                                                        </P>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                        </div>
                    </CardContent>
                )}

                {/* Session info footer - flush */}
                {latestInteraction && (
                    <div className="px-3 py-2 bg-muted/50 text-xs text-muted-foreground truncate border-t">
                        Session from{" "}
                        {format(
                            new Date(latestInteraction.sessionDate),
                            "MMM d, yyyy",
                        )}
                        : {latestInteraction.sessionTitle}
                    </div>
                )}
            </Card>
        </div>
    );
}
